/**
 * Space Entry Game — Landing zone data module
 * 24 real-world locations spanning all major continents.
 */

export interface LandingZone {
  id: string
  name: string
  latitude: number
  longitude: number
  elevation: number // meters above sea level
  biome: string
  atmosphericDensity: number // 0.5–2.0
  geographyFact: string
}

export const LANDING_ZONES: LandingZone[] = [
  {
    id: 'sahara-desert',
    name: 'Sahara Desert',
    latitude: 23.4162,
    longitude: 25.6628,
    elevation: 400,
    biome: 'Desert',
    atmosphericDensity: 0.9,
    geographyFact: 'The Sahara is the largest hot desert in the world, covering about 9.2 million square kilometers across North Africa.',
  },
  {
    id: 'amazon-rainforest',
    name: 'Amazon Rainforest',
    latitude: -3.4653,
    longitude: -62.2159,
    elevation: 80,
    biome: 'Tropical',
    atmosphericDensity: 1.8,
    geographyFact: 'The Amazon Rainforest produces about 20% of the world\'s oxygen and is home to more species of plants and animals than any other ecosystem on Earth.',
  },
  {
    id: 'siberian-tundra',
    name: 'Siberian Tundra',
    latitude: 66.5,
    longitude: 94.25,
    elevation: 200,
    biome: 'Tundra',
    atmosphericDensity: 1.3,
    geographyFact: 'The Siberian tundra stretches across northern Russia and contains vast permafrost layers that have been frozen for thousands of years.',
  },
  {
    id: 'swiss-alps',
    name: 'Swiss Alps',
    latitude: 46.8182,
    longitude: 8.2275,
    elevation: 3500,
    biome: 'Mountain',
    atmosphericDensity: 0.7,
    geographyFact: 'The Swiss Alps contain over 1,800 glaciers and include some of Europe\'s highest peaks, with the Matterhorn rising to 4,478 meters.',
  },
  {
    id: 'great-barrier-reef',
    name: 'Great Barrier Reef',
    latitude: -18.2871,
    longitude: 147.6992,
    elevation: 0,
    biome: 'Marine',
    atmosphericDensity: 1.5,
    geographyFact: 'The Great Barrier Reef is the world\'s largest coral reef system, stretching over 2,300 kilometers along Australia\'s northeast coast.',
  },
  {
    id: 'serengeti',
    name: 'Serengeti',
    latitude: -2.3333,
    longitude: 34.8333,
    elevation: 1500,
    biome: 'Savanna',
    atmosphericDensity: 1.1,
    geographyFact: 'The Serengeti hosts the largest terrestrial mammal migration in the world, with over 1.5 million wildebeest traveling annually.',
  },
  {
    id: 'himalayas',
    name: 'Himalayas',
    latitude: 27.9881,
    longitude: 86.925,
    elevation: 4500,
    biome: 'Mountain',
    atmosphericDensity: 0.5,
    geographyFact: 'The Himalayas contain all 14 of the world\'s peaks above 8,000 meters, including Mount Everest at 8,849 meters.',
  },
  {
    id: 'atacama-desert',
    name: 'Atacama Desert',
    latitude: -24.5,
    longitude: -69.25,
    elevation: 2400,
    biome: 'Desert',
    atmosphericDensity: 0.8,
    geographyFact: 'The Atacama Desert in Chile is the driest non-polar desert on Earth. Some weather stations there have never recorded rainfall.',
  },
  {
    id: 'norwegian-fjords',
    name: 'Norwegian Fjords',
    latitude: 61.5,
    longitude: 6.75,
    elevation: 0,
    biome: 'Temperate',
    atmosphericDensity: 1.4,
    geographyFact: 'Norway\'s fjords were carved by glaciers during the ice ages. Sognefjorden, the longest, extends 204 kilometers inland.',
  },
  {
    id: 'outback-australia',
    name: 'Outback Australia',
    latitude: -25.3444,
    longitude: 131.0369,
    elevation: 460,
    biome: 'Desert',
    atmosphericDensity: 1.0,
    geographyFact: 'The Australian Outback covers about 70% of the continent. Uluru, a massive sandstone monolith, rises 348 meters above the surrounding plain.',
  },
  {
    id: 'gobi-desert',
    name: 'Gobi Desert',
    latitude: 42.5,
    longitude: 103.0,
    elevation: 1500,
    biome: 'Desert',
    atmosphericDensity: 0.85,
    geographyFact: 'The Gobi Desert spans parts of China and Mongolia. It is one of the world\'s most important dinosaur fossil sites.',
  },
  {
    id: 'machu-picchu',
    name: 'Machu Picchu',
    latitude: -13.1631,
    longitude: -72.545,
    elevation: 2430,
    biome: 'Mountain',
    atmosphericDensity: 0.75,
    geographyFact: 'Machu Picchu sits at 2,430 meters in the Peruvian Andes. This 15th-century Inca citadel was unknown to the outside world until 1911.',
  },
  {
    id: 'grand-canyon',
    name: 'Grand Canyon',
    latitude: 36.1069,
    longitude: -112.1129,
    elevation: 2100,
    biome: 'Desert',
    atmosphericDensity: 0.9,
    geographyFact: 'The Grand Canyon is 446 kilometers long and up to 1,800 meters deep. Its rock layers reveal nearly 2 billion years of Earth\'s geological history.',
  },
  {
    id: 'tokyo-bay',
    name: 'Tokyo Bay',
    latitude: 35.45,
    longitude: 139.85,
    elevation: 0,
    biome: 'Temperate',
    atmosphericDensity: 1.5,
    geographyFact: 'Tokyo Bay is a major inlet on the Pacific coast of Japan. The Tokyo Bay Aqua-Line, a combined bridge-tunnel, crosses 15 kilometers of the bay.',
  },
  {
    id: 'nile-delta',
    name: 'Nile Delta',
    latitude: 31.0,
    longitude: 31.0,
    elevation: 5,
    biome: 'Wetland',
    atmosphericDensity: 1.6,
    geographyFact: 'The Nile Delta is one of the world\'s largest river deltas, spanning about 240 kilometers of Mediterranean coastline. It has supported agriculture for over 5,000 years.',
  },
  {
    id: 'patagonia',
    name: 'Patagonia',
    latitude: -47.0,
    longitude: -70.0,
    elevation: 800,
    biome: 'Steppe',
    atmosphericDensity: 1.2,
    geographyFact: 'Patagonia spans the southern tip of South America across Argentina and Chile. Its Perito Moreno Glacier is one of the few glaciers in the world that is still growing.',
  },
  {
    id: 'iceland-highlands',
    name: 'Iceland Highlands',
    latitude: 64.9631,
    longitude: -19.0208,
    elevation: 600,
    biome: 'Tundra',
    atmosphericDensity: 1.3,
    geographyFact: 'Iceland sits on the Mid-Atlantic Ridge where the North American and Eurasian tectonic plates meet. It has over 30 active volcanic systems.',
  },
  {
    id: 'borneo-rainforest',
    name: 'Borneo Rainforest',
    latitude: 1.0,
    longitude: 114.0,
    elevation: 100,
    biome: 'Tropical',
    atmosphericDensity: 1.9,
    geographyFact: 'Borneo\'s rainforest is approximately 140 million years old, making it one of the oldest in the world. It is home to orangutans, pygmy elephants, and the rafflesia flower.',
  },
  {
    id: 'kilimanjaro',
    name: 'Kilimanjaro',
    latitude: -3.0674,
    longitude: 37.3556,
    elevation: 4900,
    biome: 'Mountain',
    atmosphericDensity: 0.55,
    geographyFact: 'Mount Kilimanjaro is Africa\'s highest peak at 5,895 meters. Despite being near the equator, its summit is covered with glaciers and snow.',
  },
  {
    id: 'great-plains-usa',
    name: 'Great Plains USA',
    latitude: 41.0,
    longitude: -100.0,
    elevation: 1000,
    biome: 'Grassland',
    atmosphericDensity: 1.1,
    geographyFact: 'The Great Plains stretch from Canada to Texas across the central United States. This vast grassland is one of the most productive agricultural regions in the world.',
  },
  {
    id: 'maldives',
    name: 'Maldives',
    latitude: 3.2028,
    longitude: 73.2207,
    elevation: 1,
    biome: 'Marine',
    atmosphericDensity: 1.7,
    geographyFact: 'The Maldives is the world\'s lowest-lying country, with an average ground level of just 1.5 meters above sea level. It consists of 1,192 coral islands.',
  },
  {
    id: 'scottish-highlands',
    name: 'Scottish Highlands',
    latitude: 57.0,
    longitude: -5.0,
    elevation: 400,
    biome: 'Temperate',
    atmosphericDensity: 1.4,
    geographyFact: 'The Scottish Highlands contain some of the oldest rocks in Europe, dating back nearly 3 billion years. Ben Nevis, at 1,345 meters, is the highest peak in the British Isles.',
  },
  {
    id: 'dead-sea',
    name: 'Dead Sea',
    latitude: 31.5,
    longitude: 35.5,
    elevation: -430,
    biome: 'Desert',
    atmosphericDensity: 2.0,
    geographyFact: 'The Dead Sea is the lowest point on Earth\'s surface at 430 meters below sea level. Its extreme salinity of about 34% makes it nearly impossible for most organisms to survive.',
  },
  {
    id: 'cape-town',
    name: 'Cape Town',
    latitude: -33.9249,
    longitude: 18.4241,
    elevation: 30,
    biome: 'Mediterranean',
    atmosphericDensity: 1.3,
    geographyFact: 'Cape Town sits at the meeting point of the Atlantic and Indian Oceans. Table Mountain, its iconic flat-topped landmark, is one of the oldest mountains in the world at roughly 600 million years old.',
  },
]

/**
 * Select a random landing zone from the array.
 */
export function getRandomLandingZone(): LandingZone {
  const index = Math.floor(Math.random() * LANDING_ZONES.length)
  return LANDING_ZONES[index]
}
